import { walk } from '../../ssrHydrationHelper/shared/traverse';
import { triggerHydrationCompleteEvent } from './utils';
import {
  hydrationWrapperPrefix,
  ssrOnlyPortionIdDataKey,
} from '../shared/constants';
import SsrElement from './components/SsrElement';
import { createServerSideDefault } from '../shared/components/ServerSideDefault';

export const getHydrationWrappers = () => {
  const hydrationWrapperEntries = Object.entries(window)
    .filter(([key]) => key.includes(hydrationWrapperPrefix));
    return hydrationWrapperEntries.map(([, value]) => value);
  };

const getModuleIdFromNode = (node) => {
  const { app, name } = node.type || {};

  if (!app || !name) {
    return null;
  }

  const moduleId = `${app}/${name}`;
  return moduleId;
};

export const init = async (onAfterDeserialization, { getModule, deps }) => {
  const { React } = deps;
  const hydrationWrappers = getHydrationWrappers();
  const modulesMap = {};

  if (hydrationWrappers.length === 0) {
    return;
  }

  const prepareModules = async (content) => {
    const moduleIds = new Set();
    walk(content, (type, node) => {
      if (type === 'component') {
        const moduleId = getModuleIdFromNode(node);
        if (moduleId) {
          moduleIds.add(moduleId);
        }
      }

      return node;
    });

    await Promise.all([...moduleIds].map(async (moduleId) => {
      if (!modulesMap[moduleId]) {
        const module = await getModule(moduleId);
        const selectedModule = module?.default || null;

        modulesMap[moduleId] = selectedModule;
      }
    }));
  }

  const renderSsrNode = (node) => {
    const { ssrOnlyPortionId } = node?.meta || {};

    const wrapperElement = document.querySelector(
      `[${ssrOnlyPortionIdDataKey}="${ssrOnlyPortionId}"]`,
    );

    if (!wrapperElement) {
      return null;
    }

    const html = wrapperElement.innerHTML;
    wrapperElement.remove();

    return SsrElement({ html, ...node.props });
  };

  const renderNativeNode = (node) => {
    return React.createElement(node.type, node.props);
  };

  const renderFederatedNode = (node, moduleOnly = false) => {
    const moduleId = getModuleIdFromNode(node);
    const component = modulesMap[moduleId];

    if (!component) {
      return null;
    }

    if (moduleOnly) {
      return component;
    }

    return React.createElement(component, node.props)
  };

  const deserialiseItems = (item) => {
    return walk(item, (type, node) => {
      const { ssrOnlyPortionId, isServerSideDefault, id } = node?.meta || {};

      if (type === 'component') {
        const federatedNode = renderFederatedNode(node, isServerSideDefault);

        // Make sure to wrap in div since the server side default component
        // Renders with a div that has display: contents on the server
        if (isServerSideDefault) {
          const ServerSideDefault = createServerSideDefault(federatedNode);
          return React.createElement(ServerSideDefault, node.props);
        }

        return federatedNode;
      }

      if (type === 'native' && ssrOnlyPortionId) {
        return renderSsrNode(node);
      }

      if (type === 'native') {
        return renderNativeNode(node);
      }

      return node;
    });
  };

  const deserialisePromises = hydrationWrappers.map(async ({ id, content }) => {
    const topLevelNode = document.querySelector(`#${id}`);
    if (!topLevelNode || !content) {
      return;
    }

    await prepareModules(content);

    const deserialisedContent = deserialiseItems(content);

    await onAfterDeserialization(topLevelNode, deserialisedContent);

    return;
  });

  await Promise.all(deserialisePromises).then(triggerHydrationCompleteEvent);
};
